import cx from 'classnames'
import React from 'react'

import { Arrows, Car, Chassis, Electric, Heart } from '@cinch/oxd-components/icons/src'
import Link, { Appearance } from '@cinch/oxd-components/link/src'
import SVG from '@cinch/oxd-components/svg/src'
import { CarFront, Engine } from '@cinch/oxd-components/tech-specs-panel/src/icons'
import Text, { TextStyles } from '@cinch/oxd-components/text/src'
import Wrapper from '@cinch/oxd-components/wrapper/src'
import Layout from '@cinch/oxd-layouts/regular/src'

import Hero from './components/hero/hero.component'

import * as styles from './our-commitment.module.css'

const icons = {
  default: null,
  arrows: Arrows,
  car: Car,
  heart: Heart,
  engine: Engine,
  body: CarFront,
  chassis: Chassis,
  electrics: Electric,
}

interface AboutUsPage {
  i18n: Cinch.WebAppTranslations['ourCommitmentPage']
  bullets: React.ReactNode
  confidence: {
    title: string
    content: ContentBlock[]
  }
  vehicleDetails: ContentBlock[]
  trustedDealers: ContentBlock
}

interface ContentBlock {
  title: string
  body: React.ReactNode
  icon?: string
  styles?: any
}

const CommitmentPage: React.FC<AboutUsPage> = ({ i18n, bullets, confidence, vehicleDetails, trustedDealers }) => {
  const hero = <Hero heading={i18n.title} content={bullets} />

  return (
    <Layout hero={hero} isFluid={true}>
      <Wrapper>
        <Text className={styles.heading} type={TextStyles.vehicleSubTitle} element="h3">
          {confidence.title}
        </Text>
        <div className={styles.confidence}>
          {confidence.content.map((content) => (
            <div key={content.title} className={styles.confidenceItem}>
              <div className={styles.confidenceIconWrapper}>
                <SVG
                  width={'48%'}
                  height={'48%'}
                  className={cx(styles.confidenceIcon, styles[content.icon || 'default'])}
                  children={icons[(content.icon as keyof typeof icons) || 'default']}
                />
              </div>
              <Text type={TextStyles.vehicleSubTitle} className={styles.confidenceTitle}>
                {content.title}
              </Text>
              <div className={styles.body}>{content.body}</div>
            </div>
          ))}
        </div>
      </Wrapper>
      <Wrapper isFluid={true}>
        <div className={styles.vehicleFeatures}>
          <Wrapper>
            {vehicleDetails.map((content) => (
              <div key={content.title} className={styles.featuresItem}>
                <div className={styles.featuresIconWrapper}>
                  <SVG
                    hasGradient={true}
                    className={styles.featuresIcon}
                    children={icons[(content.icon as keyof typeof icons) || 'default']}
                  />
                </div>
                <div className={styles.featuresContent}>
                  <Text type={TextStyles.vehicleSubTitle} className={styles.featuresTitle}>
                    {content.title}
                  </Text>
                  <div className={styles.body}>{content.body}</div>
                </div>
              </div>
            ))}
          </Wrapper>
        </div>
      </Wrapper>
      <Wrapper>
        <Text className={cx(styles.heading, styles.dealersHeading)} type={TextStyles.vehicleSubTitle} element="h3">
          {trustedDealers.title}
        </Text>
        <div className={styles.dealers}>
          <div className={styles.dealersContent}>{trustedDealers.body}</div>
          <div className={styles.dealersButtons}>
            <Link
              data-testid="find"
              className={styles.link}
              appearance={Appearance.primaryButton}
              to={'/lifestyle/budget'}
            >
              <span>{i18n.find}</span>
            </Link>
            <Link data-testid="register" className={styles.link} appearance={Appearance.outlineButton} to={'/sign-up'}>
              {i18n.register}
            </Link>
          </div>
        </div>
      </Wrapper>
    </Layout>
  )
}

export default CommitmentPage
